import { useMemo, useState } from 'react';

import { useGlobalContext } from '../../context/global';
import type { IAgentChatPanelData } from '../../interface/peer-chat';

/**
 * A state observer's observers: everyone of their organisation posted in their state. A tap opens
 * the conversation. Built from api.core's observer hierarchy only — no party coverage list.
 */
const ObserverPeersPanel = () => {
	const { rsProps, agentHierarchy, peerInbox } = useGlobalContext();
	const [search, setSearch] = useState('');
	const electionId = agentHierarchy?.electionId ?? 0;

	const people = useMemo(() => {
		const term = search.trim().toLowerCase();
		const all = agentHierarchy?.children ?? [];
		const filtered = term
			? all.filter((p) => p.fullName.toLowerCase().includes(term) || p.entityCode.toLowerCase().includes(term))
			: all;
		// Unread first, then by unit code so the list reads in INEC order.
		return [...filtered].sort(
			(a, b) =>
				(peerInbox?.unreadByCounterpart.get(b.userId) ?? 0) - (peerInbox?.unreadByCounterpart.get(a.userId) ?? 0) ||
				a.entityCode.localeCompare(b.entityCode)
		);
	}, [agentHierarchy?.children, peerInbox?.unreadByCounterpart, search]);

	if (!rsProps) return null;

	if (agentHierarchy?.status === 'loading' || agentHierarchy?.status === 'idle') {
		return <p className='color-label'>Loading your observers…</p>;
	}
	if (!agentHierarchy?.children?.length) {
		return <p className='color-label'>No observers of your organisation are posted in your state yet.</p>;
	}

	const open = (userId: number) => {
		const node = agentHierarchy.children.find((n) => n.userId === userId);
		if (!node) return;
		const data: IAgentChatPanelData = {
			counterpart: {
				coreUserId: node.userId,
				name: node.fullName || 'Observer',
				entityCode: node.entityCode,
				level: node.level,
				role: 'child',
				phone: node.phoneNumber ?? undefined,
			},
			electionId,
			origin: 'inbox',
		};
		rsProps.callSection({ action: 'view', component: 'observer-chat', title: data.counterpart.name, data });
	};

	return (
		<div className='f-column-15'>
			<label className='w-100'>
				<span className='visually-hidden'>Search your observers</span>
				<input
					type='search'
					className='form-control'
					placeholder='Search by name or unit code'
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</label>
			<ul className='m-0 p-0 f-column-7'>
				{people.map((p) => {
					const unread = peerInbox?.unreadByCounterpart.get(p.userId) ?? 0;
					return (
						<li
							key={`${p.userId}:${p.entityCode}`}
							style={{ listStyle: 'none' }}
						>
							<button
								type='button'
								className='w-100 text-start border rounded p-3 bg-transparent d-flex justify-content-between align-items-center'
								style={{ minHeight: 44 }}
								onClick={() => open(p.userId)}
							>
								<span>
									<span className='d-block'>{p.fullName || 'Observer'}</span>
									<span
										className='d-block color-label'
										style={{ fontVariantNumeric: 'tabular-nums' }}
									>
										{p.entityCode}
									</span>
								</span>
								{unread > 0 ? <span className='badge rounded-pill bg-danger'>{unread}</span> : null}
							</button>
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default ObserverPeersPanel;
